import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import ClientSeoSection from '../clients/ClientSeoSection';
import { handleApiError, getImageUrl } from '../../utils/helpers';
import { Search, ChevronLeft, Users } from 'lucide-react';

const SeoPlansPage = () => {
  const { isAdmin, isSeoSpecialist } = useAuth();
  const { isRTL, t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const canAccessSeo = isAdmin || isSeoSpecialist;

  useEffect(() => {
    if (!canAccessSeo) return;
    fetchClients();
  }, [canAccessSeo]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientsAPI.getAll();
      const list = response.data.clients || [];
      setClients(list);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!canAccessSeo) {
    return (
      <div className="text-center py-12 text-slate-600">
        {isRTL ? 'غير مصرح لك بالوصول لهذا القسم' : 'You do not have access to this section'}
      </div>
    );
  }

  if (loading) return <Loading fullScreen />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-rose-600 mb-4">{error}</p>
        <Button onClick={fetchClients} variant="secondary">
          {t('tryAgain')}
        </Button>
      </div>
    );
  }

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const showBackButton = selectedClientId !== null;
  const handleBack = () => {
    if (selectedPlanId) {
      setSelectedPlanId(null);
      setSelectedPlan(null);
    } else {
      setSelectedClientId(null);
    }
  };
  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Single Header + Back button */}
      <Card className="p-5 bg-gradient-to-r from-amber-50 via-white to-sky-50 border-slate-200">
        <div className="flex items-center gap-4">
          {showBackButton && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleBack}
              className="inline-flex items-center gap-1 shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
              {isRTL ? 'رجوع' : 'Back'}
            </Button>
          )}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="flex w-10 h-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
              <Search className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">
                {t('seoPlansTitle')}
                {selectedClient && (
                  <span className="text-slate-600 font-normal">
                    {' — '}{selectedClient.company_name}
                    {selectedPlan && ` — ${selectedPlan.name}`}
                  </span>
                )}
              </h1>
              <p className="text-slate-600 text-sm mt-0.5">
                {!selectedClientId
                  ? t('seoPlansSubtitle')
                  : selectedPlanId
                  ? (isRTL ? 'جدول الكلمات المفتاحية' : 'Keywords table')
                  : (isRTL ? 'اختر خطة' : 'Select a plan')}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Client selector or SEO content */}
      {!selectedClientId ? (
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t('seoSelectClient')}
          </h2>
          {clients.length === 0 ? (
            <p className="text-slate-500 text-sm">
              {t('noClients')}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {clients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => setSelectedClientId(client.id)}
                  className="flex items-center gap-3 p-4 text-left rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all group"
                >
                  {client.logo_url && (
                    <img
                      src={getImageUrl(client.logo_url)}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">
                      {client.company_name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {client.contact_name || (isRTL ? 'بدون جهة اتصال' : 'No contact')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      ) : (
        <ClientSeoSection
          clientId={selectedClientId}
          clientName={selectedClient?.company_name}
          readOnly={false}
          showHeader={false}
          selectedPlanId={selectedPlanId}
          onPlanSelect={(planId, plan) => {
            setSelectedPlanId(planId);
            setSelectedPlan(plan || null);
          }}
        />
      )}
    </div>
  );
};

export default SeoPlansPage;
